// Playwright loads this file through Node, which needs the extension for a path inside a package
// eslint-disable-next-line import/extensions
import isEmail from 'validator/lib/isEmail.js';

export default function sanitize(userInput: string): string {
  if (typeof userInput === 'string') {
    // removing < and > from the string
    return userInput.replace(/</g, '&lt;').replace(/>/g, '&gt;');
  } return '';
}
export const formatSnapshot = (snapshot: string, longOrShort: 'long' | 'short'): string => {
  const firstDate = `20${snapshot.slice(2, 4)}-${snapshot.slice(0, 2)}-01`;
  return new Date(firstDate).toLocaleString('default', { month: longOrShort, year: 'numeric' });
};

export const camelCase = (word: string) => word.toLowerCase().replace(
  /(?:^\w|[A-Z]|\b\w)/g,
  (text, index) => (index === 0 ? text.toLowerCase() : text.toUpperCase()),
  // remove sppaces
).replace(/[\s_]+/g, '');

export const snakeCase = (word: string) => word
// Prefix captial letters with undersore
  .replace(/([A-Z])/g, '_$1')
// Replace spaces with underscores
  .replace(/ /g, '_')
// Make lowercase
  .toLowerCase()
// Remove any leading underscore were added
  .replace(/^_/, '')
// Remove duplicate underscores and replace with single underscore
  .replace(/_+/g, '_');

// Trim whitespace, convert to lowercase, replace spaces with underscores, and remove non-alphanumeric characters (except underscores and periods)
export const cleanFileName = (name: string) => name
  .trim()
  .toLowerCase()
  .replace(/\s+/g, '_')
  .replace(/[^a-z0-9_.]/g, '');

export const formatDateTimeToString = (isoString: string): string => {
  const date = new Date(isoString);
  return date.toLocaleString('default', {
    month: 'long', day: 'numeric', year: 'numeric',
  });
};

export const formatDateToString = (date: Date): string => {
  const newDate = new Date(date);
  return newDate.toLocaleString('default', { month: 'long', day: 'numeric', year: 'numeric' });
};

const poundsFormatter = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });

export const formatNumberToPounds = (amount: number): string => poundsFormatter.format(amount);

export const displayCase = (key: string): string => {
  const joiningWords = ['a', 'an', 'the', 'and', 'but', 'or', 'for', 'nor', 'as', 'at',
    'by', 'for', 'from', 'in', 'into', 'near', 'of', 'on', 'onto',
    'to', 'with'];
  const joiningWordsSet = new Set(joiningWords);

  // replace underscores with spaces
  let result = key.replace(/_/g, ' ');
  // add space before capital letters
  result = result.replace(/([A-Z])/g, ' $1');
  result = result.replace(/(\d)/g, ' $1');
  result = result.replace(/\s+/g, ' ').trim();

  return result.split(' ')
    .map((word, index) => {
      const lowerWord = word.toLocaleLowerCase();
      if (index === 0 || !joiningWordsSet.has(lowerWord)) {
        return word.charAt(0).toUpperCase() + lowerWord.slice(1);
      }
      return lowerWord;
    })
    .join(' ');
};

export const createDisplayObject = <T extends Record<string, unknown>, R = T>(object: T): R => {
  const result = {} as R;

  Object.entries(object).forEach(([key, value]) => {
    (result as Record<string, unknown>)[displayCase(key)] = value;
  });

  return result;
};

/**
 * Extracts the domain from a valid email address
 * @param email - The email address to extract domain from
 * @returns The domain part of the email, or null if invalid
 */
export function extractEmailDomain(email: string): string | null {
  if (!email || typeof email !== 'string') {
    return null;
  }

  // Validate email format first
  if (!isEmail(email)) {
    return null;
  }

  const parts = email.split('@');
  return parts.length === 2 ? parts[1] : null;
}

/**
 * Validates email and extracts domain in one step
 * @param email - The email address to validate and extract domain from
 * @returns Object with validation result and domain
 */
export function validateAndExtractDomain(email: string): {
  isValid: boolean;
  domain: string | null;
  error?: string;
} {
  if (!email || typeof email !== 'string') {
    return {
      isValid: false,
      domain: null,
      error: 'Email is required',
    };
  }

  const trimmedEmail = email.trim();

  if (!trimmedEmail) {
    return {
      isValid: false,
      domain: null,
      error: 'Email is required',
    };
  }

  if (!isEmail(trimmedEmail)) {
    return {
      isValid: false,
      domain: null,
      error: 'Please enter a valid email address',
    };
  }

  const domain = extractEmailDomain(trimmedEmail);

  return {
    isValid: true,
    domain,
  };
}

export function parseCsvIntegers(raw: string): { values: number[]; invalid: string[] } {
  const tokens = raw.split(',').map((token) => token.trim()).filter(Boolean);
  const values: number[] = [];
  const invalid: string[] = [];
  tokens.forEach((token) => {
    const parsed = Number(token);
    if (Number.isInteger(parsed) && parsed > 0) {
      values.push(parsed);
    } else {
      invalid.push(token);
    }
  });
  return { values, invalid };
}

export function parseCsvStrings(raw: string): string[] {
  return raw.split(',').map((token) => token.trim()).filter(Boolean);
}

export function parseCsvInts(value: string): number[] {
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map(Number)
    .filter((n) => !Number.isNaN(n));
}
